import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitiveBase,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts';
import type { TraderPro17LevelRole, TraderPro17LevelZone, TraderPro17OverlayResult } from './traderPro17';

export interface TraderPro17ShapeItem {
  kind: 'dot' | 'diamond' | 'triangleUp' | 'triangleDown';
  price: number;
  logicalOffset: number;
  color: string;
  text?: string;
  textPlacement?: 'right' | 'above';
  size?: number;
  textColor?: string;
  hideMarker?: boolean;
}

export interface TraderPro17ShapeRenderOptions {
  visibility?: {
    showLevelGuides?: boolean;
    showPullbackHints?: boolean;
    showAlignmentHints?: boolean;
    showAlgoTargets?: boolean;
    horizontalOffset?: number;
  };
  style?: {
    resistanceColor?: string;
    neutralColor?: string;
    supportColor?: string;
    extensionColor?: string;
    pullbackTextColor?: string;
    algoTargetColor?: string;
  };
}

type PrimitiveAttachment = SeriesAttachedParameter<Time, 'Candlestick'>;

class TraderPro17PaneRenderer implements IPrimitivePaneRenderer {
  public constructor(private readonly source: TraderPro17ShapesPrimitive) {}

  public draw(target: CanvasRenderingTarget2D): void {
    const attachment = this.source.attachment;
    if (!attachment || this.source.items.length === 0) {
      return;
    }

    const { chart, series } = attachment;
    const lastLogical = this.source.lastLogical;

    target.useMediaCoordinateSpace(({ context }) => {
      context.save();
      context.font = '12px sans-serif';
      context.textBaseline = 'middle';
      context.lineJoin = 'round';

      for (const item of this.source.items) {
        const x = chart
          .timeScale()
          .logicalToCoordinate((lastLogical + this.source.horizontalOffset + item.logicalOffset) as never);
        const y = series.priceToCoordinate(item.price);
        if (x === null || y === null) {
          continue;
        }

        const size = item.size ?? 4;
        context.fillStyle = item.color;

        if (!item.hideMarker) {
          if (item.kind === 'dot') {
            context.beginPath();
            context.arc(x, y, Math.max(1.5, size * 0.5), 0, Math.PI * 2);
            context.fill();
          } else if (item.kind === 'diamond') {
            context.save();
            context.translate(x, y);
            context.rotate(Math.PI / 4);
            context.fillRect(-size, -size, size * 2, size * 2);
            context.restore();
          } else if (item.kind === 'triangleUp') {
            context.beginPath();
            context.moveTo(x, y - size * 1.4);
            context.lineTo(x - size, y + size * 0.8);
            context.lineTo(x + size, y + size * 0.8);
            context.closePath();
            context.fill();
          } else if (item.kind === 'triangleDown') {
            context.beginPath();
            context.moveTo(x, y + size * 1.4);
            context.lineTo(x - size, y - size * 0.8);
            context.lineTo(x + size, y - size * 0.8);
            context.closePath();
            context.fill();
          }
        }

        if (item.text) {
          context.fillStyle = item.textColor ?? item.color;
          if (item.textPlacement === 'above') {
            context.textAlign = 'center';
            context.textBaseline = 'bottom';
            context.fillText(item.text, x, y - (size * 1.8 + 5));
          } else {
            context.textAlign = 'left';
            context.textBaseline = 'middle';
            context.fillText(item.text, x + size * 2 + 4, y);
          }
        }
      }

      context.restore();
    });
  }
}

class TraderPro17PaneView implements IPrimitivePaneView {
  private readonly paneRenderer: TraderPro17PaneRenderer;

  public constructor(source: TraderPro17ShapesPrimitive) {
    this.paneRenderer = new TraderPro17PaneRenderer(source);
  }

  public zOrder() {
    return 'top' as const;
  }

  public renderer(): IPrimitivePaneRenderer {
    return this.paneRenderer;
  }
}

export class TraderPro17ShapesPrimitive implements ISeriesPrimitiveBase<PrimitiveAttachment> {
  public attachment: PrimitiveAttachment | null = null;
  public items: TraderPro17ShapeItem[] = [];
  public lastLogical = 0;
  public horizontalOffset = 0;

  private readonly paneView: TraderPro17PaneView;

  public constructor() {
    this.paneView = new TraderPro17PaneView(this);
  }

  public attached(param: PrimitiveAttachment): void {
    this.attachment = param;
  }

  public detached(): void {
    this.attachment = null;
  }

  public paneViews() {
    return [this.paneView];
  }

  public setData(items: TraderPro17ShapeItem[], lastLogical: number, horizontalOffset = 0): void {
    this.items = items;
    this.lastLogical = lastLogical;
    this.horizontalOffset = horizontalOffset;
    this.attachment?.requestUpdate();
  }
}

function pushLevelSet(
  items: TraderPro17ShapeItem[],
  price: number,
  color: string,
  label: string,
  labelOffset: number,
  dotStartOffset: number,
  textPlacement: TraderPro17ShapeItem['textPlacement'] = 'above',
): void {
  for (let shift = 0; shift < 4; shift += 1) {
    items.push({
      kind: 'dot',
      price,
      logicalOffset: dotStartOffset + shift,
      color,
      size: 3,
    });
  }

  items.push({
    kind: 'diamond',
    price,
    logicalOffset: labelOffset,
    color,
    text: label,
    textPlacement,
    size: 3,
    hideMarker: true,
  });
}

function pushMessage(
  items: TraderPro17ShapeItem[],
  price: number,
  color: string,
  text: string,
  logicalOffset: number,
): void {
  items.push({
    kind: 'diamond',
    price,
    logicalOffset,
    color,
    text,
    size: 3,
    hideMarker: true,
  });
}

function hasLevelZoneAtPrice(overlay: TraderPro17OverlayResult, price: number): boolean {
  const tolerance = Math.max(Math.abs(overlay.levels8.increment) * 0.001, 1e-6);
  return overlay.levelZones.some((zone) => Math.abs(zone.price - price) <= tolerance);
}

function levelRoleLabel(role: TraderPro17LevelRole): string {
  if (role === 'support') {
    return 'Buy';
  }
  if (role === 'neutral') {
    return 'Neutral';
  }
  return 'Sell';
}

function levelRoleLabelLower(role: TraderPro17LevelRole): string {
  return role === 'support' ? 'buy' : role === 'neutral' ? 'neutral' : 'sell';
}

function levelRoleColor(
  role: TraderPro17LevelRole,
  style: {
    resistanceColor: string;
    neutralColor: string;
    supportColor: string;
  },
): string {
  if (role === 'support') {
    return style.supportColor;
  }
  if (role === 'neutral') {
    return style.neutralColor;
  }
  return style.resistanceColor;
}

function compareLevelRoles(left: TraderPro17LevelRole, right: TraderPro17LevelRole): number {
  const order: Record<TraderPro17LevelRole, number> = {
    support: 0,
    neutral: 1,
    resistance: 2,
  };
  return order[left] - order[right];
}

function levelLookbackLabel(lookback: TraderPro17LevelZone['sources'][number]['lookback']): string {
  if (lookback === 128) {
    return 'Long-term';
  }
  if (lookback === 32) {
    return 'Medium-term';
  }
  if (lookback === 16) {
    return 'Short-term';
  }
  return 'Immediate';
}

function summarizeLevelZone(
  zone: TraderPro17LevelZone,
  overlay: TraderPro17OverlayResult,
  style: {
    resistanceColor: string;
    neutralColor: string;
    supportColor: string;
  },
): { color: string; text: string } {
  const counts: Record<TraderPro17LevelRole, number> = {
    resistance: 0,
    neutral: 0,
    support: 0,
  };

  for (const source of zone.sources) {
    counts[source.role] += 1;
  }

  const breakdown = (Object.entries(counts) as Array<[TraderPro17LevelRole, number]>)
    .filter(([, count]) => count > 0)
    .sort((left, right) => {
      if (right[1] !== left[1]) {
        return right[1] - left[1];
      }
      return compareLevelRoles(left[0], right[0]);
    })
    .map(([role, count]) => `${count} ${levelRoleLabelLower(role)}`);

  const sortedCounts = (Object.entries(counts) as Array<[TraderPro17LevelRole, number]>).sort((left, right) => {
    if (right[1] !== left[1]) {
      return right[1] - left[1];
    }
    return compareLevelRoles(left[0], right[0]);
  });
  const [primaryRole, primaryCount] = sortedCounts[0];
  const secondCount = sortedCounts[1]?.[1] ?? 0;
  const activeRoleCount = breakdown.length;
  const primaryColor = levelRoleColor(primaryRole, style);

  if (activeRoleCount === 1) {
    if (primaryCount === 4) {
      return {
        color: primaryColor,
        text: `Strong ${levelRoleLabelLower(primaryRole)}`,
      };
    }

    if (primaryCount === 1) {
      return {
        color: primaryColor,
        text: `${levelLookbackLabel(zone.sources[0].lookback)} ${levelRoleLabelLower(primaryRole)}`,
      };
    }

    return {
      color: primaryColor,
      text: `${levelRoleLabel(primaryRole)} zone`,
    };
  }

  if (primaryCount === 3 || (primaryCount === 2 && secondCount === 1 && activeRoleCount === 2)) {
    return {
      color: primaryColor,
      text: `${levelRoleLabel(primaryRole)} zone (${breakdown.join(', ')})`,
    };
  }

  if (activeRoleCount === 2 && primaryCount === secondCount) {
    return {
      color: style.neutralColor,
      text: `Mixed zone (${breakdown.join(', ')})`,
    };
  }

  return {
    color: style.neutralColor,
    text: `Conflict zone (${breakdown.join(', ')})`,
  };
}

function pushLevelZoneGuide(
  items: TraderPro17ShapeItem[],
  zone: TraderPro17LevelZone,
  overlay: TraderPro17OverlayResult,
  style: {
    resistanceColor: string;
    neutralColor: string;
    supportColor: string;
  },
  dotStartOffset: number,
  labelOffset: number,
): void {
  const inactiveDotColor = '#475569';
  const lookbackOrder = [128, 32, 16, 8] as const;
  const sourceByLookback = new Map(zone.sources.map((source) => [source.lookback, source]));
  const summary = summarizeLevelZone(zone, overlay, style);

  lookbackOrder.forEach((lookback, shift) => {
    const source = sourceByLookback.get(lookback);
    items.push({
      kind: 'dot',
      price: zone.price,
      logicalOffset: dotStartOffset + shift,
      color: source ? levelRoleColor(source.role, style) : inactiveDotColor,
      size: source ? 3 : 2,
    });
  });

  items.push({
    kind: 'diamond',
    price: zone.price,
    logicalOffset: labelOffset,
    color: summary.color,
    text: summary.text,
    size: 3,
    hideMarker: true,
  });
}

export function buildTraderPro17ShapeItems(
  overlay: TraderPro17OverlayResult,
  options?: TraderPro17ShapeRenderOptions,
): TraderPro17ShapeItem[] {
  if (!overlay.allowRender) {
    return [];
  }

  const visibility = {
    showLevelGuides: options?.visibility?.showLevelGuides ?? true,
    showPullbackHints: options?.visibility?.showPullbackHints ?? true,
    showAlignmentHints: options?.visibility?.showAlignmentHints ?? true,
    showAlgoTargets: options?.visibility?.showAlgoTargets ?? true,
  };

  const style = {
    resistanceColor: options?.style?.resistanceColor ?? '#ef4444',
    neutralColor: options?.style?.neutralColor ?? '#3b82f6',
    supportColor: options?.style?.supportColor ?? '#22c55e',
    extensionColor: options?.style?.extensionColor ?? '#22c55e',
    pullbackTextColor: options?.style?.pullbackTextColor ?? '#ef4444',
    algoTargetColor: options?.style?.algoTargetColor ?? '#f97316',
  };

  const items: TraderPro17ShapeItem[] = [];

  const off = 3;
  const l128 = off + 5;
  const l32 = off + 10;
  const messageOffset = off + 35;

  if (visibility.showLevelGuides) {
    for (const zone of overlay.levelZones) {
      pushLevelZoneGuide(items, zone, overlay, style, off, l128);
    }

    pushLevelSet(items, overlay.plus28, style.resistanceColor, 'Very Strong Sell zone', l128, off, 'right');
    pushLevelSet(items, overlay.plus18, style.resistanceColor, 'Strong Sell zone', l128, off, 'right');
    pushLevelSet(items, overlay.minus18, style.extensionColor, 'Strong Buy zone', l128, off, 'right');
    pushLevelSet(items, overlay.minus28, style.extensionColor, 'Very Strong Buy zone', l128, off, 'right');
  }

  const { signals } = overlay;
  const shouldShowAlignmentMessage = (price: number) => !visibility.showLevelGuides || !hasLevelZoneAtPrice(overlay, price);

  if (visibility.showAlignmentHints) {
    if (signals.validRes100 && shouldShowAlignmentMessage(overlay.levels128.eightEight)) {
      pushMessage(items, overlay.levels128.eightEight, style.resistanceColor, 'Strong Sell zone', messageOffset);
    } else if (signals.validRes75a && shouldShowAlignmentMessage(overlay.levels128.eightEight)) {
      pushMessage(items, overlay.levels128.eightEight, style.resistanceColor, 'Sell zone', messageOffset);
    } else if (signals.validRes75b && shouldShowAlignmentMessage(overlay.levels32.eightEight)) {
      pushMessage(items, overlay.levels32.eightEight, style.resistanceColor, 'Sell zone', messageOffset);
    }

    if (signals.validNeu100 && shouldShowAlignmentMessage(overlay.levels128.fourEight)) {
      pushMessage(items, overlay.levels128.fourEight, style.neutralColor, 'Strong Neutral zone', messageOffset);
    } else if (signals.validNeu75a && shouldShowAlignmentMessage(overlay.levels128.fourEight)) {
      pushMessage(items, overlay.levels128.fourEight, style.neutralColor, 'Neutral zone', messageOffset);
    } else if (signals.validNeu75b && shouldShowAlignmentMessage(overlay.levels32.fourEight)) {
      pushMessage(items, overlay.levels32.fourEight, style.neutralColor, 'Neutral zone', messageOffset);
    }

    if (signals.validSup100 && shouldShowAlignmentMessage(overlay.levels128.zeroEight)) {
      pushMessage(items, overlay.levels128.zeroEight, style.supportColor, 'Strong Buy zone', messageOffset);
    } else if (signals.validSup75a && shouldShowAlignmentMessage(overlay.levels128.zeroEight)) {
      pushMessage(items, overlay.levels128.zeroEight, style.supportColor, 'Buy zone', messageOffset);
    } else if (signals.validSup75b && shouldShowAlignmentMessage(overlay.levels32.zeroEight)) {
      pushMessage(items, overlay.levels32.zeroEight, style.supportColor, 'Buy zone', messageOffset);
    }
  }

  if (visibility.showAlgoTargets && overlay.advancedTradingEnabled && signals.validExtTp) {
      items.push({
        kind: 'diamond',
        price: overlay.levels128.eightEight,
        logicalOffset: messageOffset,
        color: style.algoTargetColor,
        text: '>> Target <<',
        size: 3,
        hideMarker: true,
      });
  }

  if (visibility.showAlgoTargets && overlay.advancedTradingEnabled && signals.validExtTp2) {
      items.push({
        kind: 'diamond',
        price: overlay.levels32.eightEight,
        logicalOffset: messageOffset,
        color: style.algoTargetColor,
        text: '>> Target <<',
        size: 3,
        hideMarker: true,
      });
  }

  return items;
}
